/* eslint-disable */
import { useTranslation as useUiTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  updateDoc,
  doc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../../firebase";
import Swal from "sweetalert2";
import {
  Clock,
  Eye,
  EyeOff,
  MessageCircle,
  Phone,
  Mail,
  CheckCircle,
} from "lucide-react";
import API from "../../utils/api-client";

const ContactSettings = ({ isAdmin = false }) => {
  const { t: uiT } = useUiTranslation();
  const account = useSelector((state) => state.AccountReducer);
  const currentUser = isAdmin ? "adminfoorweb09" : account.user;

  const [emailConfig, setEmailConfig] = useState({
    emailHost: "",
    emailPort: 465,
    emailSecure: true,
    emailUser: "",
    emailPass: "",
    emailFrom: "",
  });

  const [qrCode, setQrCode] = useState(null);
  const [whatsappInfo, setWhatsappInfo] = useState(null);
  const [whatsappStatus, setWhatsappStatus] = useState("disconnected");

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!account.store) {
      Swal.fire({
        title: uiT("error"),
        text: uiT("ui.unknownUser"),
        icon: "error",
      });
      return;
    }

    if (!emailConfig.emailUser || !emailConfig.emailPass) {
      Swal.fire({
        title: uiT("error"),
        text: uiT("ui.emailPasswordRequired"),
        icon: "error",
      });
      return;
    }

    setIsLoading(true);

    try {
      const querySnapshot = await getDocs(
        query(collection(db, "secretKeys"), where("user", "==", account.user))
      );

      const emailData = {
        user: account.user,
        emailHost: emailConfig.emailHost || "smtp.hostinger.com",
        emailPort: emailConfig.emailPort || 465,
        emailSecure: emailConfig.emailSecure,
        emailUser: emailConfig.emailUser,
        emailPass: emailConfig.emailPass,
        emailFrom:
          emailConfig.emailFrom ||
          `"${account.store}" <${emailConfig.emailUser}>`,
        updatedAt: new Date(),
      };

      if (!querySnapshot.empty) {
        const docId = querySnapshot.docs[0].id;
        await updateDoc(doc(db, "secretKeys", docId), emailData);
      } else {
        await addDoc(collection(db, "secretKeys"), {
          ...emailData,
          createdAt: new Date(),
        });
      }

      Swal.fire({
        title: uiT("insightads.Table.facebookAds.success.title"),
        text: uiT("ui.emailSettingsSaved"),
        icon: "success",
      });
    } catch (error) {
      console.error("خطأ أثناء الحفظ:", error);
      Swal.fire({
        title: uiT("error"),
        text: uiT("servicePageActivation.countrySelector.uiTexts.alerts.errorSave"),
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };
  // 🔹 Vérifier l'état WhatsApp via API
  const checkWhatsAppStatus = async () => {
    if (!account?.user) return;

    try {
      const response = await API.post(
        "whatsapp/check-status",
        { userId: currentUser },
        { headers: { "Content-Type": "application/json" } }
      );
      const data = response.data;
      // alert(data.success);
      if (data.success) {
        if (data.status === "connected") {
          setWhatsappStatus("connected");
          setQrCode(null);
          setWhatsappInfo({
            number: account.whatsappNumber || null,
            name: account.whatsappName || null,
            connectedAt: account.connectedAt || null,
          });
        } else {
          setWhatsappStatus("waiting_scan");
          setQrCode(data.qrCode || null);
          setWhatsappInfo(null);
        }
      }
      // else {
      //   Swal.fire({
      //     icon: "error",
      //     title: "خطأ",
      //     text: data.message || "Erreur lors de la vérification de l'état",
      //     confirmButtonText: "حسناً",
      //   });
      // }
    } catch (error) {
      console.error("Erreur lors de la vérification:", error);
      setWhatsappStatus("disconnected");
      setQrCode(null);
      setWhatsappInfo(null);
    }
  };

  // 🔹 Charger la config email et vérifier l'état WhatsApp
  useEffect(() => {
    const loadEmailConfig = async () => {
      if (!account.user) {
        setIsLoadingData(false);
        return;
      }

      try {
        const querySnapshot = await getDocs(
          query(collection(db, "secretKeys"), where("user", "==", account.user))
        );

        if (!querySnapshot.empty) {
          const userSecretData = querySnapshot.docs[0].data();
          setEmailConfig({
            emailHost: userSecretData.emailHost || "smtp.hostinger.com",
            emailPort: userSecretData.emailPort || 465,
            emailSecure: userSecretData.emailSecure !== false,
            emailUser: userSecretData.emailUser || "",
            emailPass: userSecretData.emailPass || "",
            emailFrom: userSecretData.emailFrom || "",
          });
        }

        await checkWhatsAppStatus();
      } catch (error) {
        console.error("خطأ أثناء تحميل إعدادات البريد:", error);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadEmailConfig();
  }, [currentUser]);

  // 🔹 Mettre à jour WhatsApp en temps réel quand Redux change
  useEffect(() => {
    if (!currentUser) return;
    if (account.status === "connected") {
      setWhatsappStatus("connected");
      setQrCode(null);
      setWhatsappInfo({
        number: account.whatsappNumber || null,
        name: account.whatsappName || null,
        connectedAt: account.connectedAt || null,
      });
    } else if (account.status === "waiting_scan") {
      setWhatsappStatus("waiting_scan");
      setQrCode(account.qrCode || null);
      setWhatsappInfo(null);
    } else {
      setWhatsappStatus("disconnected");
      setQrCode(null);
      setWhatsappInfo(null);
    }
  }, [account.status, account.qrCode]);

  // 🔹 Connecter WhatsApp
  const connectWhatsApp = async () => {
    if (!account?.user) {
      Swal.fire({
        icon: "warning",
        title: uiT("warning"),
        text: uiT("ui.mustBeConnected"),
        confirmButtonText: uiT("ship.ok"),
      });
      return;
    }

    setWhatsappLoading(true);
    try {
      const response = await API.post(
        "whatsapp/connect",
        { userId: currentUser },
        { headers: { "Content-Type": "application/json" } }
      );

      const data = response.data;

      if (data.success) {
        if (data.status === "qr_generated") {
          setQrCode(data.qrCode);
          setWhatsappStatus("waiting_scan");
        } else if (data.status === "connected") {
          setQrCode(null);
          setWhatsappStatus("connected");
          setWhatsappInfo({
            number: account.whatsappNumber || null,
            name: account.whatsappName || null,
            connectedAt: account.connectedAt || null,
          });
        }
      } else {
        Swal.fire({
          icon: "error",
          title: uiT("error"),
          text: data.message || uiT("ui.connectionError"),
          confirmButtonText: uiT("ship.ok"),
        });
      }
    } catch (error) {
      console.error("Erreur:", error);
      Swal.fire({
        icon: "error",
        title: uiT("error"),
        text: uiT("ui.connectionError"),
        confirmButtonText: uiT("ship.ok"),
      });
    } finally {
      setWhatsappLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEmailConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  // const sendTestMessage = async () => {
  //   const phoneNumber = prompt(
  //     "Numéro de téléphone (format international sans +):"
  //   );
  //   const message = prompt("Message à envoyer:");

  //   if (!phoneNumber || !message) return;

  //   try {
  //     const response = await fetch(
  //       "https://backend.foorweb.online/api/whatsapp/send",
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //         body: JSON.stringify({
  //           userId: account.user,
  //           to: phoneNumber,
  //           message: message,
  //         }),
  //       }
  //     );

  //     const data = await response.json();

  //     if (data.success) {
  //       Swal.fire({
  //         icon: "success",
  //         title: "نجاح",
  //         text: "تم إرسال الرسالة بنجاح!",
  //         confirmButtonText: "حسناً",
  //       });
  //     } else {
  //       Swal.fire({
  //         icon: "error",
  //         title: "خطأ",
  //         text: data.error || "Erreur lors de l'envoi",
  //         confirmButtonText: "حسناً",
  //       });
  //     }
  //   } catch (error) {
  //     Swal.fire({
  //       icon: "error",
  //       title: "خطأ",
  //       text: "Erreur lors de l'envoi",
  //       confirmButtonText: "حسناً",
  //     });
  //   }
  // };

  if (isLoadingData) {
    return (
      <div className="rounded-xl p-6 mb-6 bg-white dark:!bg-[#181919] border border-transparent dark:!border-[#3c3a3d]">
        <div className="flex items-center justify-center py-8">
          <Clock size={20} className="text-[#9e68ba] animate-spin mr-2" />
          <span className="text-gray-600 dark:text-gray-400">
            {uiT("loading")}
          </span>
        </div>
      </div>
    );
  }

  if (!account.user) {
    return (
      <div className="rounded-xl p-6 mb-6 bg-white dark:!bg-[#181919] border border-transparent dark:!border-[#3c3a3d]">
        <p className="text-gray-600 dark:text-gray-400">
          {uiT("ui.connectionRequired")}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Email Settings Section */}
      <div className="rounded-xl p-6 mb-6 bg-white dark:!bg-[#181919] border border-transparent dark:!border-[#3c3a3d]">
        <div className="flex items-center mb-6">
          <Mail size={20} className="text-[#9e68ba] ml-2" />
          <h3 className="text-lg font-semibold text-gray-800 dark:!text-white">
            {uiT("ui.sendingSettings")}
          </h3>
        </div>

        <form onSubmit={handleEmailSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-[#9e68ba] mb-2">
                {uiT("ui.smtpHost")}
              </label>
              <input
                type="text"
                value={emailConfig.emailHost}
                onChange={(e) => handleInputChange("emailHost", e.target.value)}
                className="bg-white dark:!bg-[#141414] border border-gray-300 dark:!border-[#3c3a3d] text-gray-900 dark:!text-white text-sm rounded-lg block w-full py-3 px-3 focus:ring-purple-200 focus:border-purple-500"
                placeholder="smtp.hostinger.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#9e68ba] mb-2">
                {uiT("ui.smtpPort")}
              </label>
              <input
                type="number"
                value={emailConfig.emailPort}
                onChange={(e) =>
                  handleInputChange(
                    "emailPort",
                    parseInt(e.target.value) || 465
                  )
                }
                className="bg-white dark:!bg-[#141414] border border-gray-300 dark:!border-[#3c3a3d] text-gray-900 dark:!text-white text-sm rounded-lg block w-full py-3 px-3 focus:ring-purple-200 focus:border-purple-500"
                placeholder="465"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#9e68ba] mb-2">
                {uiT("specialOfferPage.validationForm.step3.fields.email")}
              </label>
              <input
                type="email"
                value={emailConfig.emailUser}
                onChange={(e) => handleInputChange("emailUser", e.target.value)}
                className="bg-white dark:!bg-[#141414] border border-gray-300 dark:!border-[#3c3a3d] text-gray-900 dark:!text-white text-sm rounded-lg block w-full py-3 px-3 focus:ring-purple-200 focus:border-purple-500"
                placeholder={uiT("workersManagement.inviteWorkerModal.emailPlaceholder")}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#9e68ba] mb-2">
                {uiT("ui.passwordRequired")}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={emailConfig.emailPass}
                  onChange={(e) =>
                    handleInputChange("emailPass", e.target.value)
                  }
                  className="bg-white dark:!bg-[#141414] border border-gray-300 dark:!border-[#3c3a3d] text-gray-900 dark:!text-white text-sm rounded-lg block w-full py-3 px-3 pr-10 focus:ring-purple-200 focus:border-purple-500"
                  placeholder={uiT("ui.emailPassword")}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff size={16} className="text-gray-400" />
                  ) : (
                    <Eye size={16} className="text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#9e68ba] mb-2">
                {uiT("ui.senderEmailOptional")}
              </label>
              <input
                type="text"
                value={emailConfig.emailFrom}
                onChange={(e) => handleInputChange("emailFrom", e.target.value)}
                className="bg-white dark:!bg-[#141414] border border-gray-300 dark:!border-[#3c3a3d] text-gray-900 dark:!text-white text-sm rounded-lg block w-full py-3 px-3 focus:ring-purple-200 focus:border-purple-500"
                placeholder={`"${account.store}" <${emailConfig.emailUser}>`}
              />
              <p className="text-xs text-gray-500 mt-1">
                {uiT("ui.leaveEmptyDefault")}
              </p>
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center">
                <input
                  id="emailSecure"
                  type="checkbox"
                  checked={emailConfig.emailSecure}
                  onChange={(e) =>
                    handleInputChange("emailSecure", e.target.checked)
                  }
                  className="w-4 h-4 text-[#9e68ba] bg-gray-100 border-gray-300 rounded focus:ring-[#9e68ba] dark:focus:ring-[#9e68ba] dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label
                  htmlFor="emailSecure"
                  className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                >
                  {uiT("ui.sslRecommended")}
                </label>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#9e68ba] hover:bg-[#8b5aa3] text-white font-medium py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <Clock size={16} className="animate-spin mr-2" />
                {uiT("navbar.try.saving")}
              </>
            ) : (
              <>
                <CheckCircle size={16} className="mr-2" />
                {uiT("adduser.save_settings")}
              </>
            )}
          </button>

          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>{uiT("validationModal.noteTitle")}</strong> {uiT("ui.smtpHelp")}
            </p>
          </div>
        </form>
      </div>
    </>
  );
};

export default ContactSettings;
